import React, { useState } from 'react';
import cx from 'classnames';

import './button-group.css';

type ToggleValue = string | number;

interface ToggleOption {
    value: ToggleValue;
    label: string | JSX.Element;
}

interface Props {
    buttonsPerRow: number;
    defaultIndex?: number;
    disabled?: boolean;
    handleChange?(value: ToggleValue): void;
    options: ToggleOption[];
}

/**
 * A grid of buttons, out of which exactly one is always pressed.
 */
export const ButtonGroup = ({
    buttonsPerRow,
    defaultIndex = 0,
    disabled = false,
    handleChange,
    options,
}: Props): JSX.Element => {
    const [activeIndex, setActiveIndex] = useState(defaultIndex);
    let numberOfGridRows = 1;

    if (options.length > 0) {
        numberOfGridRows = Math.ceil(options.length / buttonsPerRow);
    }

    const gridTemplateColumns = 'auto '.repeat(buttonsPerRow).trimEnd();
    const gridTemplateRows = 'auto '.repeat(numberOfGridRows).trimEnd();

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns,
                gridTemplateRows,
            }}
        >
            {options.map(
                (
                    { label, value }: ToggleOption,
                    index: number,
                ): JSX.Element => {
                    const active = index === activeIndex;
                    const activeClasses =
                        'active bg-primary-500 font-medium text-white';
                    const inactiveClasses =
                        'inactive border border-solid border-quarternary-100 font-medium';
                    const classNames = cx(
                        active ? activeClasses : inactiveClasses,
                        'button-group-option rounded p-2',
                    );

                    return (
                        <button
                            aria-pressed={active}
                            className={classNames}
                            disabled={disabled}
                            key={value}
                            onClick={(): void => {
                                setActiveIndex(index);
                                if (handleChange) {
                                    handleChange(value);
                                }
                            }}
                            type="button"
                        >
                            {label}
                        </button>
                    );
                },
            )}
        </div>
    );
};
